/**
 * Client-side mirror of the API's `entitateBareKey`
 * (apps/api/src/services/entitate-canonicalize.ts). Two entitate-silvică
 * values share a bare key iff they denote the same forestry entity
 * regardless of spelling / prefix variant:
 *   "IS Chișinău" / "ÎS Chișinău" / "ÎSÎS Chișinău" → "chisinau"
 *
 * Used so the admin entity picker treats spelling variants as the same
 * entity (no duplicate options, no adding the same entity twice) and
 * matches the server's dedupe-on-save — otherwise the UI lets you add
 * two variants, reports success, and the server stores only one.
 *
 * Keep in lockstep with the server helper.
 */

// Standard Moldovan forestry-entity prefixes, longest first so
// "ÎSÎS Chișinău" reduces to "Chișinău" (not "ÎS Chișinău"). RN / Icas
// are deliberately NOT stripped — they distinguish separate entities.
const ENTITY_PREFIX_RE =
  /^(?:ÎSÎS|ISIS|ÎSSC|ISSC|ÎSC|ISC|ÎS|IS|Agen[țt]ia|AGEN[ȚT]IA)\s+/i;

function normalizeKey(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip combining diacritics
    .replace(/[,;]+/g, ' ')
    .replace(/\s*-\s*/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function stripEntityPrefix(value: string): string {
  // Iterate so nested prefixes collapse fully ("ÎSÎS ÎSSC Cimişlia" →
  // "Cimişlia"). Mirrors the server helper.
  let v = value.trim();
  for (;;) {
    const next = v.replace(ENTITY_PREFIX_RE, '').trim();
    if (next === v) return v;
    v = next;
  }
}

export function entitateBareKey(value: string): string {
  return normalizeKey(stripEntityPrefix(value));
}
